import React, { useEffect, useRef, useState } from "react";
import { FaFacebook, FaVimeoV, FaTwitter, FaLinkedin } from "react-icons/fa";
import { CiSearch } from "react-icons/ci";
import { IoMdCart } from "react-icons/io";
import { Layout } from "antd";
import { gsap } from "gsap";
import axios from "axios";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import AuthModal from "./modals/authModal";
import { useCart } from "../context/CartContext";

const { Header } = Layout;

const TopBar = () => {
  const headerRef = useRef(null);
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const { cartItems } = useCart();

  const [mergedCartCount, setMergedCartCount] = useState(0);

  // 🧠 Combine Context, localStorage, and Cookie
  useEffect(() => {
    const storedCart = JSON.parse(localStorage.getItem("designCart")) || [];
    const cookieCart = Cookies.get("cartItems")
      ? JSON.parse(Cookies.get("cartItems"))
      : [];

    // Merge unique items by ID
    const merged = [...storedCart, ...cookieCart, ...cartItems];
    const unique = merged.reduce((acc, item) => {
      if (!acc.some((i) => i.id === item.id)) acc.push(item);
      return acc;
    }, []);

    setMergedCartCount(unique.length);
  }, [cartItems]);

  // 🗂️ Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get("/api/categories/fetch", {
          params: { isActive: "true", page: 1, limit: 100 },
        });
        if (response.data.success) {
          setCategories(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  // ✨ GSAP Animations
  useEffect(() => {
    if (!loading) {
      const ctx = gsap.context(() => {
        gsap.from(".topbar-header", { y: -80, opacity: 0, duration: 1 });
        gsap.from(".social-icon", {
          opacity: 0,
          y: -20,
          duration: 0.8,
          stagger: 0.2,
          delay: 0.5,
        });
        gsap.from(".main-search-bar", {
          scale: 0.8,
          opacity: 0,
          duration: 1,
          delay: 0.8,
        });
        gsap.from(".nav-link", {
          opacity: 0,
          y: 20,
          duration: 0.8,
          stagger: 0.15,
          delay: 1,
        });
      }, headerRef);
      return () => ctx.revert();
    }
  }, [loading]);

  const handleCategoryClick = (e, slug) => {
    e.preventDefault();
    navigate(`/categories/${slug}`);
  };

  return (
    <Header
      ref={headerRef}
      className="!h-auto bg-[#6CC5BF] px-0 py-0 topbar-header"
    >
      <div className="container mx-auto">
        <ul className="flex justify-between bg-[#6CC5BF] items-center text-white space-x-4 py-2">
          <li className="text-lg font-custom social-icon">
            Welcome to Initial Global Trade!
          </li>
          <li className="text-lg font-custom social-icon">
            Free shipping on all U.S. orders $50+
          </li>
          <li>
            <ul className="flex items-center gap-5 text-lg font-custom">
              <li className="social-icon">
                <a
                  href="/"
                  className="bg-white text-[#1877F2] p-[6px] rounded-full flex items-center justify-center hover:bg-[#1877F2] hover:text-white transition-colors duration-300"
                >
                  <FaFacebook />
                </a>
              </li>
              <li className="social-icon">
                <a
                  href="/"
                  className="bg-white text-[#1ab7ea] p-[6px] rounded-full flex items-center justify-center hover:bg-[#1ab7ea] hover:text-white transition-colors duration-300"
                >
                  <FaVimeoV />
                </a>
              </li>
              <li className="social-icon">
                <a
                  href="/"
                  className="bg-white text-[#0A66C2] p-[6px] rounded-full flex items-center justify-center hover:bg-[#0A66C2] hover:text-white transition-colors duration-300"
                >
                  <FaLinkedin />
                </a>
              </li>
              <li className="social-icon">
                <a
                  href="/"
                  className="bg-white text-[#1DA1F2] p-[6px] rounded-full flex items-center justify-center hover:bg-[#1DA1F2] hover:text-white transition-colors duration-300"
                >
                  <FaTwitter />
                </a>
              </li>
            </ul>
          </li>
        </ul>
      </div>

      {/* 🔍 Search Bar */}
      <div className="main-search-bar bg-white flex justify-center items-center h-20 relative">
        <input
          type="text"
          className="border rounded-3xl px-3 w-[900px] h-12 pr-10 font-custom placeholder:font-custom placeholder:text-gray-400
                 focus:outline-none focus:ring-2 focus:ring-[#6CC5BF] focus:border-[#6CC5BF]"
          placeholder="Search For Products ..."
        />
        <div className="absolute rounded-full pt-2 pb-2 pl-2 pr-2 right-[calc(50%-440px)] bg-[#6CC5BF] text-gray-500 text-xl">
          <CiSearch className="text-white text-xl" />
        </div>
      </div>

      {/* 🧭 Navigation */}
      <nav className="flex justify-center items-center bg-[#FCF5FC]">
        <ul className="flex justify-center items-center space-x-6 bg-[#FCF5FC] py-3 font-custom text-lg">
          <li>
            <a
              href="/"
              className="nav-link text-black font-medium hover:text-[#6cc5bf]"
            >
              Home
            </a>
          </li>
          <li>
            <a
              href="/designer"
              className="nav-link text-black font-medium hover:text-[#6cc5bf]"
            >
              Custom T-Shirts
            </a>
          </li>

          {/* 🏷️ Dynamic Categories */}
          {!loading && categories.length > 0 ? (
            categories.map((category) => (
              <li key={category._id}>
                <a
                  href={`/categories/${category.slug}`}
                  onClick={(e) => handleCategoryClick(e, category.slug)}
                  className="nav-link text-black font-medium hover:text-[#6cc5bf]"
                >
                  {category.name}
                </a>
              </li>
            ))
          ) : loading ? (
            <li className="nav-link text-black font-medium">Loading...</li>
          ) : null}

          <li>
            <a
              href="/services"
              className="nav-link text-black font-medium hover:text-[#6cc5bf]"
            >
              Services
            </a>
          </li>
          <li>
            <a
              href="/"
              className="nav-link text-black font-medium hover:text-[#6cc5bf]"
            >
              Help Center
            </a>
          </li>
          <li>
            <ul className="flex items-center gap-6 pl-4">
              <AuthModal />

              {/* 🛒 Cart */}
              <li className="nav-link relative">
                <a href="/cart" className="relative">
                  <IoMdCart className="text-black hover:text-[#6cc5bf] transition-colors text-2xl" />
                  {mergedCartCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-[#6cc5bf] text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                      {mergedCartCount}
                    </span>
                  )}
                </a>
              </li>

              <li className="nav-link">
                <a
                  href="/"
                  className="text-black hover:text-[#6cc5bf] transition-colors text-2xl"
                >
                  Let's Talk
                </a>
              </li>
            </ul>
          </li>
        </ul>
      </nav>
    </Header>
  );
};

export default TopBar;
